import { SelectField, TextField, useFormContext, useFormField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Grid, GridItem } from '@calendly/ui/components/grid'
import { BodyText } from '@calendly/ui/components/typography'

import { getSummary } from './get_summary'

const getV2Summary = ({ spotStep, customSpotStep, customSpotStepType }) => {
  const time = spotStep === 'custom' ? customSpotStep : spotStep
  const type = spotStep === 'custom' ? customSpotStepType : I18n.t('time.min_abbr')

  return `${time} ${type}`
}

const PRESET_INCREMENT_STEPS = [5, 10, 15, 20, 30, 45, 60]

const getIncrementOptionName = ({ step, v3Style, mins }) => (
  v3Style
    ? getSummary({ spotStep: String(step), customSpotStep: '', customSpotStepType: 'minutes', v3Style })
    : `${step} ${mins}`
)

const LockedIncrementInfo = ({ v3Style }) => {
  const { values: { customSpotStepType, customSpotStep, spotStep } } = useFormContext()

  return v3Style ? (
    getSummary({ spotStep, customSpotStep, customSpotStepType, v3Style })
  ) : (
    <BodyText data-testid="locked-increment">
      {getV2Summary({ spotStep, customSpotStep, customSpotStepType })}
    </BodyText>
  )
}

const IncrementsField = ({ v3Style }) => {
  const { values: { spotStep } } = useFormContext()
  const [customSpotStepFieldProps, customSpotStepMeta] = useFormField({ name: 'customSpotStep' })

  const mins = I18n.t('time.min_abbr')

  const incrementOptions = [
    ...PRESET_INCREMENT_STEPS.map((step) => ({
      id: step,
      name: getIncrementOptionName({ step, v3Style, mins }),
    })),
    { id: 'custom', name: I18n.t('publisher.event_types.availability.duration_fieldset.custom') },
  ]
  const unitSelectOptions = [
    { id: 'minutes', name: I18n.t('time.min_abbr') },
    { id: 'hours', name: I18n.t('time.hours_abbr.one') },
  ]

  return (
    <Grid data-testid="increment-form" spacing={v3Style ? 0 : 8}>
      <GridItem size={v3Style ? 12 : 10} sizeMobile={12}>
        <Grid spacing={v3Style ? 0 : 8}>
          <GridItem size={12}>
            <SelectField
              aria-labelledby="incrementSelectLabel"
              name="spotStep"
              options={incrementOptions}
              data-component="increments"
            />
          </GridItem>
          {spotStep === 'custom' && (
            <>
              <GridItem size={7}>
                <TextField
                  {...customSpotStepFieldProps}
                  aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_value_label')}
                  aria-invalid={customSpotStepMeta.touched && !!customSpotStepMeta.error}
                  inputMode="numeric"
                />
              </GridItem>
              <GridItem size={5}>
                <SelectField
                  aria-label={I18n.t('publisher.event_types.one_off.creation_modal.time_step.duration.custom_unit_label')}
                  name="customSpotStepType"
                  options={unitSelectOptions}
                />
              </GridItem>
            </>
          )}
        </Grid>
      </GridItem>
    </Grid>
  )
}

/**
 * A dropdown used to configure an Event Type's Start time increments
 * */
export const Increments = ({ isLocked, ...props }) => (
  !isLocked ? <IncrementsField {...props} /> : <LockedIncrementInfo {...props} />
)
